import React from "react";
import * as io from "io-ts";
import classNames from "classnames";
import deepEqual from "fast-deep-equal";
import { SectionContainer } from "@ui/Section";
import TableCard from "@ui/Table/Card";
import { FilterWrapper, buildOptions } from "@ui/Filter";
import FilterSelect, { type FilterOption } from "@ui/Filter/FilterSelect";
import { ResetElement } from "@ui/Filter/Reset";
import LoadingBoundary from "@ui/LoadingBoundary";
import uiT from "@ui/i18n";
import t from "../../../i18n";
import useQuery, { useRevalidate } from "../../../api/useQuery";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { Project } from "../../../api/useProjects";
import createGlobalState from "@ui/lib/createGlobalState";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import { useChannel } from "../../../api/ws/useChannel";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import ProcessesOverviewTables from "./ProcessesOverviewTables";

const FILTER_ALL = "__ALL__";
const PROJECT_FILTER_ANY = "__ANY_PROJECT__";
const UPDATED_SINCE_ANY = "ANY";
const UPDATED_SINCE_LAST_30_MINUTES = "LAST_30_MINUTES";
const UPDATED_SINCE_LAST_6_HOURS = "LAST_6_HOURS";
const UPDATED_SINCE_LAST_DAY = "LAST_DAY";
const UPDATED_SINCE_LAST_WEEK = "LAST_WEEK";

const PROCESS_STATUSES = ["STARTABLE", "PENDING", "RUNNING", "DONE"] as const;
const PROCESS_TYPES = [
  "SIMULATION",
  "DIAGNOSIS",
  "EXTERNAL_GAP",
  "DIRECT_SIMULATION",
  "SIMULATION_MODEL_VALIDATION",
] as const;

type ProcessStatus = (typeof PROCESS_STATUSES)[number];
type ProcessType = (typeof PROCESS_TYPES)[number];
type TypeFilterValue = ProcessType | typeof FILTER_ALL;
type StatusFilterValue = ProcessStatus | typeof FILTER_ALL;
type UpdatedSinceFilterValue =
  | typeof UPDATED_SINCE_ANY
  | typeof UPDATED_SINCE_LAST_30_MINUTES
  | typeof UPDATED_SINCE_LAST_6_HOURS
  | typeof UPDATED_SINCE_LAST_DAY
  | typeof UPDATED_SINCE_LAST_WEEK;
type ProjectFilterValue = string | typeof PROJECT_FILTER_ANY;

const TypeFilterValueCodec = io.union([
  io.literal(FILTER_ALL),
  io.literal("SIMULATION"),
  io.literal("DIAGNOSIS"),
  io.literal("EXTERNAL_GAP"),
  io.literal("DIRECT_SIMULATION"),
  io.literal("SIMULATION_MODEL_VALIDATION"),
]);

const StatusFilterValueCodec = io.union([
  io.literal(FILTER_ALL),
  io.literal("STARTABLE"),
  io.literal("PENDING"),
  io.literal("RUNNING"),
  io.literal("DONE"),
]);

const UpdatedSinceFilterValueCodec = io.union([
  io.literal(UPDATED_SINCE_ANY),
  io.literal(UPDATED_SINCE_LAST_30_MINUTES),
  io.literal(UPDATED_SINCE_LAST_6_HOURS),
  io.literal(UPDATED_SINCE_LAST_DAY),
  io.literal(UPDATED_SINCE_LAST_WEEK),
]);

const ProcessesOverviewFilters = io.strict({
  typeFilterValues: io.array(TypeFilterValueCodec),
  statusFilterValues: io.array(StatusFilterValueCodec),
  projectFilterValues: io.array(io.string),
  updatedSinceFilterValues: io.array(UpdatedSinceFilterValueCodec),
});

type ProcessesOverviewFilters = io.TypeOf<typeof ProcessesOverviewFilters>;

const DEFAULT_PROCESSES_OVERVIEW_FILTERS: ProcessesOverviewFilters = {
  typeFilterValues: [FILTER_ALL],
  statusFilterValues: ["PENDING", "RUNNING"],
  projectFilterValues: [PROJECT_FILTER_ANY],
  updatedSinceFilterValues: [UPDATED_SINCE_LAST_30_MINUTES],
};

const useProcessesOverviewFilters = createGlobalState({
  storage: "session",
  key: "administrationProcessesOverviewFilters",
  type: ProcessesOverviewFilters,
  defaultValue: DEFAULT_PROCESSES_OVERVIEW_FILTERS,
  scoped: false,
});

const typeOptions = buildOptions<TypeFilterValue>([
  { value: FILTER_ALL, label: t("administration.processes.filters.all") },
  ...PROCESS_TYPES.map<FilterOption<TypeFilterValue>>(type => ({
    value: type,
    label: type,
  })),
]);

const statusOptions = buildOptions<StatusFilterValue>([
  { value: FILTER_ALL, label: t("administration.processes.filters.all") },
  ...PROCESS_STATUSES.map<FilterOption<StatusFilterValue>>(status => ({
    value: status,
    label: status,
  })),
]);

const updatedSinceOptions = buildOptions<UpdatedSinceFilterValue>([
  {
    value: UPDATED_SINCE_ANY,
    label: t("administration.processes.filters.updated_since.any"),
  },
  {
    value: UPDATED_SINCE_LAST_30_MINUTES,
    label: t("administration.processes.filters.updated_since.last_30_minutes"),
  },
  {
    value: UPDATED_SINCE_LAST_6_HOURS,
    label: t("administration.processes.filters.updated_since.last_6_hours"),
  },
  {
    value: UPDATED_SINCE_LAST_DAY,
    label: t("administration.processes.filters.updated_since.last_day"),
  },
  {
    value: UPDATED_SINCE_LAST_WEEK,
    label: t("administration.processes.filters.updated_since.last_week"),
  },
]);

function normalizeFilterSelection<T extends string>(
  previous: T[],
  next: T[],
  allValue: T,
  fallbackOnEmpty: T[],
) {
  if (next.includes(allValue)) {
    if (previous.includes(allValue) && next.length > 1)
      return next.filter(value => value !== allValue);
    return [allValue];
  }
  if (next.length === 0) return fallbackOnEmpty;
  return next;
}

function normalizeSingleFilterSelection<T extends string>(
  previous: T[],
  next: T[],
  fallbackValue: T,
) {
  if (next.length === 0) {
    return previous[0] === undefined ? [fallbackValue] : [previous[0]];
  }

  const addedValue = next.find(value => !previous.includes(value));
  if (addedValue !== undefined) return [addedValue];

  return [next[0] ?? fallbackValue];
}

export default function ProcessesDashboard() {
  const revalidate = useRevalidate();
  const revalidateProcesses = useDebouncedCallback(
    () => {
      void revalidate("/processes/overview");
    },
    [revalidate],
    2_000,
    {
      leading: true,
      maxWait: 2_000,
      trailing: true,
    },
  );

  useChannel({ key: "processes" }, revalidateProcesses, true);

  return (
    <SectionContainer>
      <ProcessesMonitoringSection />
    </SectionContainer>
  );
}

function ProcessesMonitoringSection() {
  const projectsResponse = useQuery("/projects", { suspend: false });
  const projects = React.useMemo<Project[]>(
    () => [...(projectsResponse?.body.collection ?? [])].sort(comparatorBy("name")),
    [projectsResponse?.body.collection],
  );
  const [filters, setFilters] = useProcessesOverviewFilters({});
  const typeFilterValues = filters.typeFilterValues as TypeFilterValue[];
  const statusFilterValues = filters.statusFilterValues as StatusFilterValue[];
  const projectFilterValues = filters.projectFilterValues as ProjectFilterValue[];
  const updatedSinceFilterValues = filters.updatedSinceFilterValues as UpdatedSinceFilterValue[];

  const selectedTypes = React.useMemo(
    () => (typeFilterValues.includes(FILTER_ALL) ? [] : (typeFilterValues as ProcessType[])),
    [typeFilterValues],
  );
  const selectedStatuses = React.useMemo(
    () => (statusFilterValues.includes(FILTER_ALL) ? [] : (statusFilterValues as ProcessStatus[])),
    [statusFilterValues],
  );
  const selectedProjectIds = React.useMemo(
    () =>
      projectFilterValues.includes(PROJECT_FILTER_ANY) ? [] : (projectFilterValues as string[]),
    [projectFilterValues],
  );
  const hasProjectFilter = selectedProjectIds.length > 0;
  const selectedUpdatedSince = React.useMemo(
    () => updatedSinceFilterValues[0] ?? UPDATED_SINCE_LAST_30_MINUTES,
    [updatedSinceFilterValues],
  );

  const projectOptions = React.useMemo(
    () =>
      buildOptions<ProjectFilterValue>([
        { value: PROJECT_FILTER_ANY, label: t("administration.processes.filters.any") },
        ...projects.map(project => ({ value: project.id, label: project.name })),
      ]),
    [projects],
  );
  const projectNameById = React.useMemo(
    () =>
      projects.reduce<Record<string, string>>((acc, project) => {
        acc[project.id] = project.name;
        return acc;
      }, {}),
    [projects],
  );

  const displayedTypes = React.useMemo(
    () =>
      selectedTypes.length === 0
        ? [...PROCESS_TYPES]
        : PROCESS_TYPES.filter(type => selectedTypes.includes(type)),
    [selectedTypes],
  );
  const displayedStatuses = React.useMemo(
    () => (selectedStatuses.length === 0 ? [...PROCESS_STATUSES] : selectedStatuses),
    [selectedStatuses],
  );
  const isResetActive = React.useMemo(
    () => !deepEqual(filters, DEFAULT_PROCESSES_OVERVIEW_FILTERS),
    [filters],
  );

  return (
    <TableCard
      title={t("administration.processes.overview.title")}
      headerLeft={
        <FilterWrapper className="wrappable">
          <FilterSelect<TypeFilterValue>
            title={t("administration.processes.filters.type")}
            options={typeOptions}
            values={typeFilterValues}
            setValues={values =>
              setFilters(previous => ({
                ...previous,
                typeFilterValues: normalizeFilterSelection(
                  previous.typeFilterValues,
                  values,
                  FILTER_ALL,
                  [FILTER_ALL] as TypeFilterValue[],
                ),
              }))
            }
            filtered
          />
          <FilterSelect<StatusFilterValue>
            title={t("administration.processes.filters.status")}
            options={statusOptions}
            values={statusFilterValues}
            setValues={values =>
              setFilters(previous => ({
                ...previous,
                statusFilterValues: normalizeFilterSelection(
                  previous.statusFilterValues,
                  values,
                  FILTER_ALL,
                  [] as StatusFilterValue[],
                ),
              }))
            }
            filtered
          />
          <FilterSelect<ProjectFilterValue>
            title={t("administration.processes.overview.project")}
            options={projectOptions}
            values={projectFilterValues}
            setValues={values =>
              setFilters(previous => ({
                ...previous,
                projectFilterValues: normalizeFilterSelection(
                  previous.projectFilterValues,
                  values,
                  PROJECT_FILTER_ANY,
                  [PROJECT_FILTER_ANY] as ProjectFilterValue[],
                ),
              }))
            }
            filtered
          />
          <FilterSelect<UpdatedSinceFilterValue>
            title={t("administration.processes.filters.updated_since")}
            options={updatedSinceOptions}
            values={[selectedUpdatedSince]}
            setValues={values =>
              setFilters(previous => ({
                ...previous,
                updatedSinceFilterValues: normalizeSingleFilterSelection(
                  previous.updatedSinceFilterValues,
                  values,
                  UPDATED_SINCE_LAST_30_MINUTES,
                ),
              }))
            }
            filtered
          />
          <ResetElement
            onClick={() => setFilters(DEFAULT_PROCESSES_OVERVIEW_FILTERS)}
            className={classNames({ active: isResetActive }, HIDE_ON_EXPORT)}
          >
            {uiT("filter.reset")}
          </ResetElement>
        </FilterWrapper>
      }
      headerRight={null}
    >
      <LoadingBoundary>
        <ProcessesOverviewTables
          displayedStatuses={displayedStatuses}
          displayedTypes={displayedTypes}
          hasProjectFilter={hasProjectFilter}
          projectNameById={projectNameById}
          selectedUpdatedSince={selectedUpdatedSince}
          selectedProjectIds={selectedProjectIds}
          selectedStatuses={selectedStatuses}
          selectedTypes={selectedTypes}
          setProjectFilterValues={values =>
            setFilters(previous => ({
              ...previous,
              projectFilterValues: values,
            }))
          }
        />
      </LoadingBoundary>
    </TableCard>
  );
}
